import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Card } from '@/components/ui/card';
import { Plus, Edit, Trash2, User, RotateCcw } from 'lucide-react';
import { PATIENTS } from '@/constants/testIds';
import { toast } from 'sonner';
import FileUpload from '@/components/FileUpload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Avatar component for patient photos
const PatientAvatar = ({ photoFileId, name }) => {
  const [photoUrl, setPhotoUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPhoto = async () => {
      if (photoFileId) {
        try {
          const response = await axios.get(`${API}/files/${photoFileId}`, {
            withCredentials: true,
            responseType: 'blob'
          });
          const url = URL.createObjectURL(response.data);
          setPhotoUrl(url);
        } catch (error) {
          console.error('Error loading photo:', error);
        }
      }
      setLoading(false);
    };

    fetchPhoto();

    return () => {
      if (photoUrl) {
        URL.revokeObjectURL(photoUrl);
      }
    };
  }, [photoFileId]);

  if (loading && photoFileId) {
    return (
      <div className="w-12 h-12 rounded-full bg-gray-200 animate-pulse flex-shrink-0" />
    );
  }

  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={name}
        className="w-12 h-12 rounded-full object-cover flex-shrink-0 border-2 border-gray-200"
      />
    );
  }

  return (
    <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
      <User className="w-6 h-6 text-gray-500" />
    </div>
  );
};

const PatientsPage = () => {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPatient, setEditingPatient] = useState(null);
  const [showTrash, setShowTrash] = useState(false);
  const [trashedPatients, setTrashedPatients] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    date_of_birth: '',
    email: '',
    diagnosis: '',
    medical_history: '',
    allergies: '',
    medications: '',
    emergency_contact: '',
    emergency_phone: '',
    emergency_email: '',
    photo_file_id: null,
  });

  useEffect(() => {
    fetchPatients();
  }, []);

  const fetchPatients = async () => {
    try {
      const { data } = await axios.get(`${API}/patients`, { withCredentials: true });
      setPatients(data || []);
    } catch (error) {
      console.error('Error fetching patients:', error);
      toast.error(error.response?.data?.detail || 'Error al cargar pacientes');
      setPatients([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPatient) {
        await axios.put(`${API}/patients/${editingPatient.id}`, formData, { withCredentials: true });
        toast.success('Paciente actualizado');
      } else {
        await axios.post(`${API}/patients`, formData, { withCredentials: true });
        toast.success('Paciente creado');
      }
      setDialogOpen(false);
      resetForm();
      fetchPatients();
    } catch (error) {
      toast.error('Error al guardar paciente');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Enviar este paciente a la papelera? Podrás restaurarlo durante 30 días.')) return;
    try {
      await axios.delete(`${API}/patients/${id}`, { withCredentials: true });
      toast.success('Paciente movido a la papelera');
      fetchPatients();
      if (showTrash) fetchTrash();
    } catch (error) {
      console.error('Error deleting patient:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar paciente');
    }
  };

  const fetchTrash = async () => {
    try {
      const { data } = await axios.get(`${API}/patients/trash`, { withCredentials: true });
      setTrashedPatients(data || []);
    } catch (error) {
      setTrashedPatients([]);
    }
  };

  const toggleTrash = () => {
    const next = !showTrash;
    setShowTrash(next);
    if (next) fetchTrash();
  };

  const handleRestore = async (id) => {
    try {
      await axios.post(`${API}/patients/${id}/restore`, {}, { withCredentials: true });
      toast.success('Paciente restaurado');
      fetchTrash();
      fetchPatients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al restaurar');
    }
  };

  const handlePurge = async (id) => {
    if (!window.confirm('¿Eliminar este paciente DEFINITIVAMENTE? Esta acción no se puede deshacer.')) return;
    try {
      await axios.delete(`${API}/patients/${id}/purge`, { withCredentials: true });
      toast.success('Paciente eliminado permanentemente');
      fetchTrash();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      date_of_birth: '',
      email: '',
      diagnosis: '',
      medical_history: '',
      allergies: '',
      medications: '',
      emergency_contact: '',
      emergency_phone: '',
      emergency_email: '',
      photo_file_id: null,
    });
    setEditingPatient(null);
  };

  const openEditDialog = (patient) => {
    setEditingPatient(patient);
    setFormData({
      name: patient.name || '',
      date_of_birth: patient.date_of_birth || '',
      email: patient.email || '',
      diagnosis: patient.diagnosis || '',
      medical_history: patient.medical_history || '',
      allergies: patient.allergies || '',
      medications: patient.medications || '',
      emergency_contact: patient.emergency_contact || '',
      emergency_phone: patient.emergency_phone || '',
      emergency_email: patient.emergency_email || '',
      photo_file_id: patient.photo_file_id || null,
    });
    setDialogOpen(true);
  };

  if (loading) return <div>Cargando...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-semibold" style={{ color: '#2D3748', fontFamily: 'Manrope' }}>
            Pacientes
          </h1>
          <p className="text-sm mt-1" style={{ color: '#718096' }}>
            Gestiona los pacientes de tu institución
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={showTrash ? 'default' : 'outline'}
            onClick={toggleTrash}
            className="flex items-center gap-2"
            data-testid="patients-trash-toggle"
          >
            <Trash2 size={18} />
            Papelera{showTrash && trashedPatients.length ? ` (${trashedPatients.length})` : ''}
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
          <DialogTrigger asChild>
            <Button data-testid={PATIENTS.addBtn} className="btn-primary flex items-center gap-2">
              <Plus size={20} />
              Nuevo Paciente
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingPatient ? 'Editar Paciente' : 'Nuevo Paciente'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <FileUpload
                label="Foto del Paciente"
                fileType="photo"
                accept="image/*"
                maxSize={5}
                currentFileId={formData.photo_file_id}
                onFileUploaded={(fileId) => setFormData({ ...formData, photo_file_id: fileId })}
                onFileRemoved={() => setFormData({ ...formData, photo_file_id: null })}
              />
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Nombre Completo *</Label>
                  <Input
                    id="name"
                    data-testid={PATIENTS.nameInput}
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="dob">Fecha de Nacimiento</Label>
                  <Input
                    id="dob"
                    type="date"
                    value={formData.date_of_birth}
                    onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="email">Email del Paciente</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="mt-1"
                  placeholder="Para recibir recordatorios de citas"
                />
              </div>
              <div>
                <Label htmlFor="diagnosis">Diagnóstico</Label>
                <Textarea
                  id="diagnosis"
                  value={formData.diagnosis}
                  onChange={(e) => setFormData({ ...formData, diagnosis: e.target.value })}
                  rows={3}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="history">Historia Médica</Label>
                <Textarea
                  id="history"
                  value={formData.medical_history}
                  onChange={(e) => setFormData({ ...formData, medical_history: e.target.value })}
                  rows={3}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="allergies">Alergias</Label>
                <Input
                  id="allergies"
                  value={formData.allergies}
                  onChange={(e) => setFormData({ ...formData, allergies: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="medications">Medicamentos</Label>
                <Input
                  id="medications"
                  value={formData.medications}
                  onChange={(e) => setFormData({ ...formData, medications: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="emergency-contact">Contacto de Emergencia</Label>
                  <Input
                    id="emergency-contact"
                    value={formData.emergency_contact}
                    onChange={(e) => setFormData({ ...formData, emergency_contact: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="emergency-phone">Teléfono de Emergencia</Label>
                  <Input
                    id="emergency-phone"
                    type="tel"
                    value={formData.emergency_phone}
                    onChange={(e) => setFormData({ ...formData, emergency_phone: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="emergency-email">Email de Contacto de Emergencia</Label>
                <Input
                  id="emergency-email"
                  type="email"
                  value={formData.emergency_email}
                  onChange={(e) => setFormData({ ...formData, emergency_email: e.target.value })}
                  className="mt-1"
                  placeholder="Email alternativo para notificaciones"
                />
              </div>
              <Button type="submit" data-testid={PATIENTS.saveBtn} className="btn-primary w-full">
                {editingPatient ? 'Actualizar' : 'Crear'} Paciente
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {showTrash && (
        <Card className="p-6 border-amber-200" data-testid="patients-trash-panel">
          <div className="flex items-center gap-2 mb-1">
            <Trash2 className="h-5 w-5 text-amber-600" />
            <h2 className="text-lg font-semibold text-amber-800">Papelera de Pacientes ({trashedPatients.length})</h2>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Los pacientes eliminados se conservan 30 días y luego se borran automáticamente.
          </p>
          {trashedPatients.length === 0 ? (
            <p className="text-sm text-gray-500">No hay pacientes en la papelera.</p>
          ) : (
            <div className="space-y-2">
              {trashedPatients.map((p) => (
                <div key={p.id} className="flex items-center justify-between border rounded-lg px-4 py-3" data-testid={`trash-patient-${p.id}`}>
                  <div>
                    <p className="font-medium" style={{ color: '#2D3748' }}>{p.name}</p>
                    <p className="text-xs text-gray-500">
                      {p.days_remaining ?? 30} días restantes
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                      onClick={() => handleRestore(p.id)}
                      data-testid={`restore-patient-${p.id}`}
                    >
                      <RotateCcw className="h-4 w-4 mr-1" />
                      Restaurar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => handlePurge(p.id)}
                      data-testid={`purge-patient-${p.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid={PATIENTS.list}>
        {patients.map((patient) => (
          <Card key={patient.id} className="p-6 hover:-translate-y-1 transition-transform duration-200">
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-3">
                <PatientAvatar 
                  photoFileId={patient.photo_file_id} 
                  name={patient.name} 
                />
                <div>
                  <h3 className="text-lg font-semibold" style={{ color: '#2D3748', fontFamily: 'Manrope' }}>
                    {patient.name}
                  </h3>
                  {patient.date_of_birth && (
                    <p className="text-sm" style={{ color: '#718096' }}>
                      Nacimiento: {patient.date_of_birth}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => openEditDialog(patient)}
                  className="hover:bg-[#E5E2DC]"
                >
                  <Edit size={16} style={{ color: '#5A7D6C' }} />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(patient.id)}
                  data-testid={PATIENTS.deleteBtn}
                  className="hover:bg-red-50"
                >
                  <Trash2 size={16} style={{ color: '#F44336' }} />
                </Button>
              </div>
            </div>
            {patient.diagnosis && (
              <div className="mb-2">
                <p className="text-xs font-medium" style={{ color: '#718096' }}>
                  Diagnóstico
                </p>
                <p className="text-sm" style={{ color: '#2D3748' }}>
                  {patient.diagnosis}
                </p>
              </div>
            )}
            {patient.emergency_contact && (
              <div>
                <p className="text-xs font-medium" style={{ color: '#718096' }}>
                  Contacto de Emergencia
                </p>
                <p className="text-sm" style={{ color: '#2D3748' }}>
                  {patient.emergency_contact} {patient.emergency_phone && `- ${patient.emergency_phone}`}
                </p>
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
};

export default PatientsPage;
